// A bundle whose contents are picked on its product page can only be split into its products at
// checkout if the picks came with it (_bundle_selection). Apps that add to cart by variant alone -
// the account's "recently viewed" and "buy again", upsells - send it without them, and the
// warehouse gets a bundle with no contents. Such an add is refused before it is sent (so an app's
// "add and go to checkout" stops too) and the bundle's quick buy opens for the shopper to pick the
// contents. Lines that got into the cart some other way are taken out on the next page load.
(() => {
  if (window.bundleCartGuard) return;
  window.bundleCartGuard = true;

  const root = window.Shopify?.routes?.root || '/';
  const variantsKey = 'bundle-cart-guard-variants';
  const pendingKey = 'bundle-cart-guard-pending';
  const { fetch } = window;

  let bundleVariants = null;

  // Variant id -> { url, title } for every pick-your-contents bundle, once per session.
  const loadBundleVariants = () => {
    bundleVariants ??= (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(variantsKey));
        if (cached) return cached;
      } catch {}

      try {
        const data = await fetch.call(window, `${root}collections/all?view=bundle-variants`).then((response) => response.json());
        try {
          sessionStorage.setItem(variantsKey, JSON.stringify(data.variants));
        } catch {}
        return data.variants;
      } catch {
        return {};
      }
    })();

    return bundleVariants;
  };

  const configure = (bundles) => {
    const [first, ...rest] = bundles;
    const id = `bundle-cart-guard-${Date.now()}`;
    const scheme = document.body.dataset.defaultColorScheme || '';

    // Parsed rather than created: the theme's dialog sets attributes in its constructor, which
    // document.createElement does not allow.
    document.body.insertAdjacentHTML('beforeend', `<quick-buy-modal id="${id}" class="modal modal--quick-buy bundle-cart-guard-modal color-scheme color-scheme--${scheme}"></quick-buy-modal>`);
    const modal = document.getElementById(id);
    modal.setAttribute('product-url', first.url);

    // Right above the bundle's choices, put back if the theme re-renders the popup.
    const addNote = () => {
      const builder = modal.querySelector('bundle-builder');
      if (!builder || modal.querySelector('.bundle-cart-guard__note')) return;

      const note = document.createElement('p');
      note.className = 'bundle-cart-guard__note text-sm';
      note.textContent = 'Choose what goes in your bundle, then add it to your cart.';
      builder.before(note);
    };

    new MutationObserver(addNote).observe(modal, { childList: true, subtree: true });

    // Rebuy opens its drawer again when it sees the cart change; the bundle's popup goes on top.
    const keepDrawerClosed = () => {
      if (modal.hasAttribute('open')) window.Rebuy?.SmartCart?.hide?.();
    };
    document.addEventListener('rebuy:smartcart.show', keepDrawerClosed);
    setTimeout(() => document.removeEventListener('rebuy:smartcart.show', keepDrawerClosed), 4000);

    // The account app's product window (recently viewed, buy again) stays open over the page, inside
    // the app's shadow root; it only closes once its own add has finished, so it is closed just after.
    const closeAppWindows = () => {
      const roots = [document, ...[...document.querySelectorAll('*')].map((element) => element.shadowRoot).filter(Boolean)];
      roots.forEach((root) => root.querySelectorAll('.product-action-modal__close').forEach((button) => button.click()));
    };
    setTimeout(closeAppWindows, 300);
    setTimeout(closeAppWindows, 1200);

    window.Rebuy?.SmartCart?.hide?.();
    modal.show().then(addNote);

    if (rest.length) {
      const links = rest.map((info) => `<a href="${info.url}" class="link">${info.title}</a>`).join(', ');
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: { message: `Please also choose the contents of: ${links}.`, duration: 10000 },
      }));
    }
  };

  // The items of a cart/add body: form data, url-encoded or JSON, one item or several.
  const readItems = (body) => {
    if (!body) return [];

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        return Array.isArray(json.items) ? json.items : [json];
      } catch {
        body = new URLSearchParams(body);
      }
    }

    if (body instanceof FormData || body instanceof URLSearchParams) {
      return [{ id: body.get('id'), properties: { _bundle_selection: body.get('properties[_bundle_selection]') } }];
    }

    return [];
  };

  const unchosen = async (items) => {
    const variants = await loadBundleVariants();
    return items
      .filter((item) => variants[String(item.id)] && !item.properties?._bundle_selection)
      .map((item) => variants[String(item.id)]);
  };

  // The cart page shows lines rendered by the server, so there the bundle opens after a reload.
  const reopen = () => {
    try {
      const pending = JSON.parse(sessionStorage.getItem(pendingKey));
      sessionStorage.removeItem(pendingKey);
      if (pending?.length) configure(pending);
    } catch {}
  };

  const handOver = (bundles) => {
    if (window.themeVariables?.settings?.pageType !== 'cart') {
      configure(bundles);
      return;
    }

    try {
      sessionStorage.setItem(pendingKey, JSON.stringify(bundles));
    } catch {}
    window.location.reload();
  };

  let running = null;

  // Takes out lines that reached the cart without their picks.
  const sweep = () => {
    running ??= (async () => {
      try {
        const cart = await fetch.call(window, `${root}cart.js`).then((response) => response.json());
        const removed = [];

        for (const item of cart.items) {
          const [bundle] = await unchosen([{ id: item.variant_id, properties: item.properties }]);
          if (!bundle) continue;

          await fetch.call(window, `${root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
          removed.push(bundle);
        }

        if (!removed.length) return;

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.Rebuy?.Cart?.fetchCart?.();
        handOver(removed);
      } catch {
      } finally {
        running = null;
      }
    })();

    return running;
  };

  const cartPath = (resource) => {
    try {
      const url = new URL(String(resource), window.location.href);
      if (url.origin !== window.location.origin) return null;
      return url.pathname.match(/\/cart\/(add|update)(\.js)?$/)?.[1] || null;
    } catch {
      return null;
    }
  };

  window.fetch = function (resource, init, ...rest) {
    const path = cartPath(resource instanceof Request ? resource.url : resource);
    if (!path) return fetch.call(this, resource, init, ...rest);

    return (async () => {
      if (path === 'add' && !(resource instanceof Request)) {
        const bundles = await unchosen(readItems(init?.body));
        if (bundles.length) {
          configure(bundles);
          return new Response(JSON.stringify({ status: 400, message: 'Choose the bundle contents first.' }), {
            status: 400,
            headers: { 'Content-Type': 'application/json' },
          });
        }
      }

      const response = await fetch.call(this, resource, init, ...rest);
      sweep();
      return response;
    })();
  };

  const { open } = XMLHttpRequest.prototype;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (cartPath(url)) this.addEventListener('load', () => sweep());
    return open.call(this, method, url, ...rest);
  };

  const start = () => {
    loadBundleVariants();
    reopen();
    sweep();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
